/**
 * アクティビティ見直しの水位線バー
 *
 * 「どこまで見直したか」を表示し、対象日までを確認済みにする。
 */

import { CircleCheck, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatDateCompact } from '@/lib/format-utils'
import { useActivityReviewStore } from '@/store/useActivityReviewStore'
import { useAppStore } from '@/store/useAppStore'

type Props = {
  /** 「この日まで確認済み」の対象日（YYYY-MM-DD）。省略時はボタンを出さず表示のみ */
  targetDate?: string
  /** 確認済みの日へ移動する。未指定なら日付をタップしても何も起きない */
  onJump?: (date: string) => void
  /** 移動先のデータを読み込み中 */
  isJumping?: boolean
}

export const ReviewProgressBar = ({ targetDate, onJump, isJumping = false }: Props) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const reviewedThroughDate = useActivityReviewStore((state) => state.reviewedThroughDate)
  const unreviewedDays = useActivityReviewStore((state) => state.unreviewedDays)
  const isFetched = useActivityReviewStore((state) => state.isFetched)
  const setReviewedThrough = useActivityReviewStore((state) => state.setReviewedThrough)
  const { isSubmitting, run } = useSubmitLock()

  if (!hasFullAccess) return null
  // 取得前に「未確認」と出して直後に書き換わるのを防ぐ
  if (!isFetched) return null

  const isTargetReviewed = !!targetDate && !!reviewedThroughDate && targetDate <= reviewedThroughDate
  // 確認済みの日には出す情報がないので丸ごと隠す
  if (isTargetReviewed) return null

  // 表示中の日が既に確認済みの日なら移動する意味がない
  const canJump = !!onJump && !!reviewedThroughDate && reviewedThroughDate !== targetDate

  const handleMarkReviewed = async () => {
    if (!targetDate) return
    await run(async () => {
      const succeeded = await setReviewedThrough(targetDate)
      if (succeeded) {
        toast.success(`${formatDateCompact(targetDate)} まで確認済みにしました`)
      } else {
        toast.error('確認済みの更新に失敗しました')
      }
    })
  }

  return (
    <div className="text-muted-foreground flex items-center gap-1.5 border-b bg-muted/30 px-3 py-1.5 text-xs">
      {isJumping ? (
        <Loader2 className="size-3.5 shrink-0 animate-spin" />
      ) : (
        <CircleCheck className="size-3.5 shrink-0" />
      )}
      {!reviewedThroughDate ? (
        <span className="truncate">未確認</span>
      ) : canJump ? (
        <button
          type="button"
          className="shrink-0 underline decoration-dotted underline-offset-2 disabled:opacity-60"
          onClick={() => onJump?.(reviewedThroughDate)}
          disabled={isJumping}
        >
          {formatDateCompact(reviewedThroughDate)} まで
        </button>
      ) : (
        <span className="shrink-0">{formatDateCompact(reviewedThroughDate)} まで</span>
      )}
      {unreviewedDays > 0 && <span className="truncate">・残り {unreviewedDays}日</span>}
      {targetDate && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-6 shrink-0 px-2 text-xs"
          onClick={handleMarkReviewed}
          disabled={isSubmitting}
        >
          {isSubmitting ? <Loader2 className="size-3.5 animate-spin" /> : null}
          ここまで確認
        </Button>
      )}
    </div>
  )
}
